/**
 * Organisation admin — buses, drivers, passengers, and high-level overview.
 * Drivers are onboarded via the shared registration link with ?role=driver (see in-page instructions).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { supabase } from '../supabase';
import { useAuth } from '../authContext';
import { BUS_TYPE_OPTIONS, DRIVER_LICENSE_OPTIONS, licenseCanDrive, licenseTypeLabel, ASSIGNMENT_LABELS } from '../lib/transport';
import { AGE_RANGE_LABELS } from '../lib/passengers';
import { getDriverLicenseUrl } from '../lib/driverLicense';
import {
  adminMovePassengerBus,
  adminRebalanceArea,
  adminSetBusArea,
  adminSetBusDriver,
  adminSetPassengerStatus,
} from '../lib/adminAssignments';
import { localTodayStr } from '../lib/date';
import { RECURRENCE_OPTIONS, WEEKDAY_LABELS, isTripSlotActiveOnDate, tripSlotRecurrenceLabel } from '../lib/tripSlots';

const accent = '#0d7377';
const bg = '#0f1419';
const card = '#1a2332';
const text = '#e8eaed';
const muted = '#9aa0a6';

function timeToHHMM(t) {
  if (!t) return '';
  // Supabase `time` may come as "HH:MM:SS" (or sometimes "HH:MM").
  return String(t).slice(0, 5);
}

function parseTimeToDateToday(timeStr, now = new Date()) {
  if (!timeStr) return null;
  const parts = String(timeStr).split(':');
  const hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  const d = new Date(now);
  d.setHours(hh, mm, 0, 0);
  return d;
}

export default function AdminDashboard() {
  const { profile, signOutUser } = useAuth();
  const navigate = useNavigate();

  const [org, setOrg] = useState(null);
  const [inviteCode, setInviteCode] = useState('');
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [goingToday, setGoingToday] = useState([]);
  const [areaDraftsByBusId, setAreaDraftsByBusId] = useState({});
  const [programs, setPrograms] = useState([]);
  const [programDraftsByBusId, setProgramDraftsByBusId] = useState({});
  const [nowTick, setNowTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [inviteDraft, setInviteDraft] = useState('');
  const [inviteSaving, setInviteSaving] = useState(false);

  // Trip slots
  const [tripSlots, setTripSlots] = useState([]);
  const [slotLabel, setSlotLabel] = useState('');
  const [slotTime, setSlotTime] = useState('08:00');
  const [slotDate, setSlotDate] = useState('');
  const [slotRecurrence, setSlotRecurrence] = useState('daily');
  const [slotWeekdays, setSlotWeekdays] = useState([0]);
  const [slotDayOfMonth, setSlotDayOfMonth] = useState('1');
  const [driverIssues, setDriverIssues] = useState([]);
  const [driverNames, setDriverNames] = useState({});
  const [guestPassengers, setGuestPassengers] = useState([]);
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestArea, setGuestArea] = useState('');
  const [guestAddress, setGuestAddress] = useState('');
  const [guestAgeRange, setGuestAgeRange] = useState('adult');
  const [guestSaving, setGuestSaving] = useState(false);
  const [adminMessage, setAdminMessage] = useState('');
  const [adminMessageTarget, setAdminMessageTarget] = useState('');
  const [sendingAdminMessage, setSendingAdminMessage] = useState(false);

  // Bus form
  const [busLabel, setBusLabel] = useState('');
  const [busPlate, setBusPlate] = useState('');
  const [busRoute, setBusRoute] = useState('');
  const [busType, setBusType] = useState('manual');
  const [busSeats, setBusSeats] = useState('14');

  const loadAll = useCallback(async () => {
    if (!profile?.org_id) return;
    setError('');

    const { data: orgRow, error: orgErr } = await supabase
      .from('organizations')
      .select('id, name, slug')
      .eq('id', profile.org_id)
      .single();

    if (orgErr) {
      setError(orgErr.message);
      return;
    }
    setOrg(orgRow);

    const { data: secretRow, error: secretErr } = await supabase
      .from('organization_secrets')
      .select('invite_code')
      .eq('org_id', profile.org_id)
      .maybeSingle();

    if (secretErr) {
      setError(secretErr.message);
      return;
    }
    setInviteCode(secretRow?.invite_code || '');

    const { data: busRows, error: busErr } = await supabase
      .from('buses')
      .select('id, label, plate_number, route_name, is_available, driver_id, route_started_at, bus_type, seat_capacity, service_area')
      .eq('org_id', profile.org_id)
      .order('label');

    if (busErr) {
      setError(busErr.message);
      return;
    }
    setBuses(busRows || []);

    const { data: profRows, error: pErr } = await supabase
      .from('profiles')
      .select('id, full_name, role, active, driver_online, approval_status, driver_license_type, driver_license_file_path, pickup_address, pickup_area, age_range, phone')
      .eq('org_id', profile.org_id)
      .in('role', ['driver', 'passenger']);

    if (pErr) {
      setError(pErr.message);
      return;
    }

    const d = (profRows || []).filter((p) => p.role === 'driver');
    const pass = (profRows || []).filter((p) => p.role === 'passenger');
    setDrivers(d);
    setPassengers(pass);

    const tripDate = localTodayStr();
    const { data: goingRows, error: goingErr } = await supabase
      .from('daily_going')
      .select(
        'id, passenger_id, guest_passenger_id, bus_id, assignment_status, seat_count, party_total, party_infants, trip_slot_id',
      )
      .eq('trip_date', tripDate)
      .eq('going', true);

    if (goingErr) {
      setError(goingErr.message);
      return;
    }
    setGoingToday(goingRows || []);

    setAreaDraftsByBusId(Object.fromEntries((busRows || []).map((b) => [b.id, b.service_area || ''])));

    // Programmes (scheduled departures per bus).
    // If the table/columns aren't present yet, we don't block the rest of the admin dashboard.
    try {
      const { data: progRows, error: progErr } = await supabase
        .from('programs')
        .select('id, bus_id, departure_time')
        .eq('org_id', profile.org_id);

      if (progErr) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load programs:', progErr.message);
        setPrograms([]);
        setProgramDraftsByBusId({});
      } else {
        const rows = progRows || [];
        setPrograms(rows);
        setProgramDraftsByBusId(Object.fromEntries(rows.map((p) => [p.bus_id, timeToHHMM(p.departure_time)])));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load programs:', e);
      setPrograms([]);
      setProgramDraftsByBusId({});
    }

    try {
      const { data: slotRows, error: slotErr } = await supabase
        .from('org_trip_slots')
        .select('id, trip_date, start_time, label, recurrence_type, days_of_week, day_of_month')
        .eq('org_id', profile.org_id)
        .order('start_time');

      if (!slotErr) {
        setTripSlots(slotRows || []);
      }
    } catch {
      setTripSlots([]);
    }

    try {
      const { data: issueRows, error: issueErr } = await supabase
        .from('driver_issues')
        .select('id, driver_id, bus_id, message, created_at, read_at')
        .eq('org_id', profile.org_id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (!issueErr) {
        setDriverIssues(issueRows || []);
        const driverIds = [...new Set((issueRows || []).map((r) => r.driver_id))];
        if (driverIds.length) {
          const { data: dprofs } = await supabase.from('profiles').select('id, full_name').in('id', driverIds);
          setDriverNames(Object.fromEntries((dprofs || []).map((d) => [d.id, d.full_name])));
        } else {
          setDriverNames({});
        }
      }
    } catch {
      setDriverIssues([]);
    }

    try {
      const { data: guestRows, error: guestErr } = await supabase
        .from('org_guest_passengers')
        .select('id, full_name, phone, pickup_area, pickup_address, age_range, active')
        .eq('org_id', profile.org_id)
        .order('full_name');

      if (!guestErr) {
        setGuestPassengers(guestRows || []);
      }
    } catch {
      setGuestPassengers([]);
    }
  }, [profile?.org_id]);

  useEffect(() => {
    loadAll().finally(() => setLoading(false));
  }, [loadAll]);

  useEffect(() => {
    const t = setInterval(() => setNowTick((x) => x + 1), 20000);
    return () => clearInterval(t);
  }, []);

  const programsByBusId = useMemo(() => Object.fromEntries((programs || []).map((p) => [p.bus_id, p])), [programs]);

  async function upsertBusDepartureTime(busId) {
    if (!profile?.org_id) return;
    setError('');

    const hhmm = (programDraftsByBusId[busId] ?? '').trim();
    if (!hhmm) {
      setError('Please choose a departure time.');
      return;
    }
    const timeValue = hhmm.length === 5 ? `${hhmm}:00` : hhmm;

    const existing = programsByBusId[busId];
    if (existing?.id) {
      const { error: err } = await supabase.from('programs').update({ departure_time: timeValue }).eq('id', existing.id);
      if (err) {
        setError(err.message);
        return;
      }
    } else {
      const { error: err } = await supabase.from('programs').insert({
        org_id: profile.org_id,
        bus_id: busId,
        departure_time: timeValue,
      });
      if (err) {
        setError(err.message);
        return;
      }
    }

    await loadAll();
  }

  async function addBus(e) {
    e.preventDefault();
    if (!profile?.org_id) return;
    setError('');
    const seats = parseInt(busSeats, 10);
    if (Number.isNaN(seats) || seats < 1) {
      setError('Seat capacity must be at least 1.');
      return;
    }
    const { error: err } = await supabase.from('buses').insert({
      org_id: profile.org_id,
      label: busLabel.trim(),
      plate_number: busPlate.trim() || null,
      route_name: busRoute.trim() || null,
      bus_type: busType,
      seat_capacity: seats,
      is_available: true,
      driver_id: null,
    });
    if (err) {
      setError(err.message);
      return;
    }
    setBusLabel('');
    setBusPlate('');
    setBusRoute('');
    setBusType('manual');
    setBusSeats('14');
    await loadAll();
  }

  async function removeBus(id) {
    if (!window.confirm('Remove this bus?')) return;
    setError('');
    const { error: err } = await supabase.from('buses').delete().eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  async function toggleBusAvailability(bus) {
    setError('');
    const { error: err } = await supabase.from('buses').update({ is_available: !bus.is_available }).eq('id', bus.id);
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  async function viewDriverLicense(driverId, filePath) {
    if (!filePath) {
      setError('No licence file uploaded.');
      return;
    }
    setError('');
    const { url, error: urlErr } = await getDriverLicenseUrl(filePath);
    if (urlErr || !url) {
      setError(urlErr?.message || 'Could not open licence file.');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  const passengersByArea = useMemo(() => {
    const groups = {};
    for (const p of passengers) {
      const area = p.pickup_area?.trim() || 'No area set';
      if (!groups[area]) groups[area] = [];
      groups[area].push(p);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [passengers]);

  async function handleAssignDriver(busId, driverId) {
    setError('');
    const { error: err } = await adminSetBusDriver(busId, driverId || null);
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  async function handleSaveBusArea(busId) {
    setError('');
    const area = areaDraftsByBusId[busId] ?? '';
    const { error: err } = await adminSetBusArea(busId, area);
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  async function handleMovePassenger(passengerId, busId) {
    setError('');
    const { error: err } = await adminMovePassengerBus(passengerId, localTodayStr(), busId || null);
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  async function handlePassengerStatus(passengerId, status) {
    setError('');
    const { error: err } = await adminSetPassengerStatus(passengerId, localTodayStr(), status);
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  async function handleRebalanceArea(area) {
    setError('');
    const { error: err } = await adminRebalanceArea(area, localTodayStr());
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  function driversForBus(bus) {
    return drivers.filter(
      (d) =>
        d.approval_status === 'approved' &&
        d.active &&
        licenseCanDrive(d.driver_license_type, bus.bus_type),
    );
  }

  const passengerById = useMemo(() => Object.fromEntries(passengers.map((p) => [p.id, p])), [passengers]);
  const guestById = useMemo(() => Object.fromEntries(guestPassengers.map((g) => [g.id, g])), [guestPassengers]);

  const goingPersonLabel = useCallback(
    (row) => {
      if (row.guest_passenger_id) {
        const guest = guestById[row.guest_passenger_id];
        return guest ? `${guest.full_name} (guest)` : 'Guest passenger';
      }
      return passengerById[row.passenger_id]?.full_name || 'Passenger';
    },
    [guestById, passengerById],
  );

  const goingPersonArea = useCallback(
    (row) => {
      if (row.guest_passenger_id) {
        return guestById[row.guest_passenger_id]?.pickup_area || '—';
      }
      return passengerById[row.passenger_id]?.pickup_area || '—';
    },
    [guestById, passengerById],
  );

  const goingByBusId = useMemo(() => {
    const map = {};
    for (const row of goingToday) {
      if (!row.bus_id) continue;
      if (!map[row.bus_id]) map[row.bus_id] = [];
      map[row.bus_id].push(row);
    }
    return map;
  }, [goingToday]);

  const unassignedGoing = useMemo(
    () => goingToday.filter((r) => !r.bus_id),
    [goingToday],
  );

  const areasGoingToday = useMemo(() => {
    const areas = new Set();
    for (const row of goingToday) {
      const area = goingPersonArea(row)?.trim();
      if (area && area !== '—') areas.add(area);
    }
    return [...areas].sort((a, b) => a.localeCompare(b));
  }, [goingToday, goingPersonArea]);

  const guestGoingByGuestAndSlot = useMemo(() => {
    const map = {};
    for (const row of goingToday) {
      if (!row.guest_passenger_id || !row.trip_slot_id) continue;
      map[`${row.guest_passenger_id}:${row.trip_slot_id}`] = row;
    }
    return map;
  }, [goingToday]);

  const busSeatUsage = useCallback(
    (busId) => {
      const rows = goingByBusId[busId] || [];
      const confirmed = rows.filter((r) => r.assignment_status === 'confirmed');
      const seats = confirmed.reduce((sum, r) => sum + (r.seat_count || 1), 0);
      return { seats, passengers: rows.length, confirmed: confirmed.length, waitlisted: rows.length - confirmed.length };
    },
    [goingByBusId],
  );

  async function updateDriverLicense(driverId, licenseType) {
    setError('');
    const { error: err } = await supabase
      .from('profiles')
      .update({ driver_license_type: licenseType })
      .eq('id', driverId);
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  async function reviewDriver(driverId, approve) {
    setError('');
    const { error: err } = await supabase
      .from('profiles')
      .update({
        approval_status: approve ? 'approved' : 'rejected',
        active: approve,
      })
      .eq('id', driverId);
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  async function deactivateDriver(driverId, active) {
    setError('');
    const { error: err } = await supabase.from('profiles').update({ active }).eq('id', driverId);
    if (err) {
      setError(err.message);
      return;
    }
    if (!active) {
      await supabase.from('buses').update({ driver_id: null }).eq('driver_id', driverId);
    }
    await loadAll();
  }

  async function addTripSlot(e) {
    e.preventDefault();
    if (!profile?.org_id) return;
    setError('');

    if (slotRecurrence === 'weekly' && slotWeekdays.length === 0) {
      setError('Select at least one weekday for a weekly trip.');
      return;
    }
    if (slotRecurrence === 'once' && !slotDate.trim()) {
      setError('Choose a date for a one-time trip.');
      return;
    }

    const tripDate =
      slotRecurrence === 'once' ? slotDate.trim() : slotRecurrence === 'daily' ? null : slotDate.trim() || null;

    const { error: err } = await supabase.from('org_trip_slots').insert({
      org_id: profile.org_id,
      label: slotLabel.trim() || 'Trip',
      start_time: slotTime.length === 5 ? `${slotTime}:00` : slotTime,
      trip_date: tripDate,
      recurrence_type: slotRecurrence,
      days_of_week: slotRecurrence === 'weekly' ? slotWeekdays.slice().sort((a, b) => a - b) : null,
      day_of_month: slotRecurrence === 'monthly' ? parseInt(slotDayOfMonth, 10) || 1 : null,
    });
    if (err) {
      setError(err.message);
      return;
    }
    setSlotLabel('');
    setSlotTime('08:00');
    setSlotDate('');
    setSlotRecurrence('daily');
    setSlotWeekdays([0]);
    setSlotDayOfMonth('1');
    await loadAll();
  }

  function toggleSlotWeekday(day) {
    setSlotWeekdays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b),
    );
  }

  async function removeTripSlot(id) {
    if (!window.confirm('Remove this trip start time?')) return;
    setError('');
    const { error: err } = await supabase.from('org_trip_slots').delete().eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  const todayActiveSlots = useMemo(() => {
    const tripDate = localTodayStr();
    return (tripSlots || []).filter((slot) => isTripSlotActiveOnDate(slot, tripDate));
  }, [tripSlots]);

  async function addGuestPassenger(e) {
    e.preventDefault();
    if (!profile?.org_id) return;
    setError('');
    setGuestSaving(true);
    const { error: err } = await supabase.rpc('admin_add_guest_passenger', {
      p_full_name: guestName.trim(),
      p_pickup_area: guestArea.trim(),
      p_phone: guestPhone.trim() || null,
      p_pickup_address: guestAddress.trim() || null,
      p_age_range: guestAgeRange,
    });
    setGuestSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setGuestName('');
    setGuestPhone('');
    setGuestArea('');
    setGuestAddress('');
    setGuestAgeRange('adult');
    await loadAll();
  }

  async function removeGuestPassenger(id) {
    if (!window.confirm('Remove this unregistered passenger?')) return;
    setError('');
    const { error: err } = await supabase.rpc('admin_delete_guest_passenger', { p_guest_id: id });
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  async function toggleGuestGoing(guestId, slotId, going) {
    setError('');
    const { error: err } = await supabase.rpc('admin_mark_guest_going', {
      p_guest_id: guestId,
      p_going: going,
      p_trip_date: localTodayStr(),
      p_trip_slot_id: slotId,
      p_party_total: 1,
      p_party_infants: 0,
    });
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  async function sendAdminMessage(e) {
    e.preventDefault();
    if (!adminMessage.trim()) return;
    setError('');
    setSendingAdminMessage(true);
    const { error: err } = await supabase.rpc('admin_send_passenger_message', {
      p_message: adminMessage.trim(),
      p_passenger_id: adminMessageTarget || null,
    });
    setSendingAdminMessage(false);
    if (err) {
      setError(err.message);
      return;
    }
    setAdminMessage('');
    setAdminMessageTarget('');
  }

  async function markIssueRead(id) {
    setError('');
    const { error: err } = await supabase
      .from('driver_issues')
      .update({ read_at: new Date().toISOString() })
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    await loadAll();
  }

  async function handleSignOut() {
    await signOutUser();
    navigate('/login', { replace: true });
  }

  async function changeInviteCode(e) {
    e.preventDefault();
    const next = inviteDraft.trim();
    if (!profile?.org_id || !next) return;

    setError('');
    setInviteSaving(true);
    const { error: err } = await supabase
      .from('organization_secrets')
      .update({ invite_code: next.toUpperCase() })
      .eq('org_id', profile.org_id);
    if (err) {
      setError(err.message);
      setInviteSaving(false);
      return;
    }
    setInviteDraft('');
    await loadAll();
    setInviteSaving(false);
  }

  const pendingDrivers = drivers.filter((d) => d.approval_status === 'pending');
  const approvedDrivers = drivers.filter((d) => d.approval_status !== 'pending');

  const driverInviteUrl =
    typeof window !== 'undefined' && org
      ? `${window.location.origin}/register/${encodeURIComponent(org.slug)}?role=driver`
      : '';

  const orgRegisterUrl =
    typeof window !== 'undefined' && org ? `${window.location.origin}/register/${encodeURIComponent(org.slug)}` : '';

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'trips', label: 'Trips' },
    { id: 'assignments', label: 'Assignments' },
    { id: 'buses', label: 'Buses' },
    { id: 'drivers', label: 'Drivers' },
    { id: 'passengers', label: 'Passengers' },
    { id: 'messages', label: 'Messages' },
    { id: 'access', label: 'Access' },
  ];

  if (loading) {
    return (
      <div style={shell}>
        <p style={{ color: muted }}>Loading admin workspace…</p>
      </div>
    );
  }

  return (
    <div style={shell}>
      <header style={headerBar}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'clamp(1.25rem, 4vw, 1.5rem)' }}>Admin</h1>
          <p style={{ margin: '4px 0 0', color: muted, fontSize: '0.9rem' }}>
            {org?.name || 'Organisation'} · Buses, drivers, and passengers
          </p>
        </div>
        <button type="button" onClick={handleSignOut} style={ghostBtn}>
          Sign out
        </button>
      </header>

      {error ? (
        <p style={{ color: '#f28b82', marginBottom: 12 }} role="alert">
          {error}
        </p>
      ) : null}

      {/* Tabs */}
      <nav style={tabsBar} aria-label="Admin dashboard tabs">
        {tabs.map((t) => {
          const active = activeTab === t.id;
          return (
            <button key={t.id} type="button" onClick={() => setActiveTab(t.id)} style={active ? tabBtnActive : tabBtn}>
              {t.label}
            </button>
          );
        })}
      </nav>

      {/* Overview */}
      {activeTab === 'overview' ? (
        <section style={section}>
          <h2 style={h2}>Overview</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
            }}
          >
            <StatCard label="Buses" value={buses.length} />
            <StatCard label="Drivers" value={drivers.length} />
            <StatCard label="Passengers" value={passengers.length} />
            <StatCard label="Available buses" value={buses.filter((b) => b.is_available).length} />
          </div>

          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ color: muted, fontSize: '0.9rem', marginBottom: 2 }}>Driver status</div>
            {(drivers || []).map((dr) => {
              const bus = buses.find((b) => b.driver_id === dr.id) || null;
              const prog = bus ? programsByBusId[bus.id] : null;
              const now = new Date(Date.now() + nowTick * 0); // depend on tick

              const started = !!bus?.route_started_at;
              const depDate = prog?.departure_time ? parseTimeToDateToday(prog.departure_time, now) : null;
              const late = !started && depDate ? now.getTime() > depDate.getTime() + 10 * 60 * 1000 : false;

              const status = started
                ? { color: '#81c995', text: 'Started on time' }
                : late
                  ? { color: '#f28b82', text: 'Late - not started' }
                  : depDate
                    ? { color: '#fbbc04', text: 'Due to depart soon' }
                    : { color: muted, text: 'Departure not set' };

              return (
                <div
                  key={dr.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                    background: 'rgba(0,0,0,0.25)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 12,
                    padding: '12px 14px',
                  }}
                >
                  <div>
                    <strong style={{ fontSize: '1rem' }}>{dr.full_name || 'Driver'}</strong>
                    <div style={{ color: muted, fontSize: '0.86rem', marginTop: 4 }}>
                      {bus ? `Bus: ${bus.label}` : 'No bus assigned'}
                      {prog?.departure_time ? ` · Departs at ${timeToHHMM(prog.departure_time)}` : ''}
                    </div>
                  </div>
                  <div style={{ fontWeight: 800, color: status.color }}>{status.text}</div>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Trips — org-wide start times */}
      {activeTab === 'trips' ? (
        <section style={section}>
          <h2 style={h2}>Trip start times</h2>
          <p style={{ color: muted, fontSize: '0.9rem', marginTop: 0 }}>
            Schedule trip start times with daily, weekly, monthly, or one-time frequency. Passengers can mark going until
            90 minutes before each start time.
          </p>
          <form onSubmit={addTripSlot} style={{ ...cardBox, marginBottom: 16 }}>
            <div style={formGrid}>
              <div>
                <label style={label}>Label</label>
                <input value={slotLabel} onChange={(e) => setSlotLabel(e.target.value)} style={input} placeholder="Morning pickup" />
              </div>
              <div>
                <label style={label}>Start time</label>
                <input type="time" required value={slotTime} onChange={(e) => setSlotTime(e.target.value)} style={input} />
              </div>
              <div>
                <label style={label}>Frequency</label>
                <select value={slotRecurrence} onChange={(e) => setSlotRecurrence(e.target.value)} style={input}>
                  {RECURRENCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {slotRecurrence === 'once' ? (
                <div>
                  <label style={label}>Date</label>
                  <input type="date" required value={slotDate} onChange={(e) => setSlotDate(e.target.value)} style={input} />
                </div>
              ) : null}
              {slotRecurrence === 'weekly' ? (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={label}>Days of week</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                    {WEEKDAY_LABELS.map((day) => {
                      const active = slotWeekdays.includes(day.value);
                      return (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleSlotWeekday(day.value)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: active ? `2px solid ${accent}` : '1px solid rgba(255,255,255,0.12)',
                            background: active ? 'rgba(13,115,119,0.2)' : 'transparent',
                            color: text,
                            cursor: 'pointer',
                            fontWeight: active ? 700 : 500,
                          }}
                        >
                          {day.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              {slotRecurrence === 'monthly' ? (
                <div>
                  <label style={label}>Day of month (1–31)</label>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    required
                    value={slotDayOfMonth}
                    onChange={(e) => setSlotDayOfMonth(e.target.value)}
                    style={input}
                  />
                </div>
              ) : null}
            </div>
            <button type="submit" style={primaryBtn}>
              Add trip
            </button>
          </form>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tripSlots.length === 0 ? (
              <p style={{ color: muted }}>No trips scheduled — add start times above.</p>
            ) : (
              tripSlots.map((slot) => (
                <div key={slot.id} style={cardBox}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <div>
                      <strong>{slot.label}</strong>
                      <div style={{ color: muted, fontSize: '0.88rem', marginTop: 4 }}>
                        {timeToHHMM(slot.start_time)} · {tripSlotRecurrenceLabel(slot)}
                      </div>
                    </div>
                    <button type="button" onClick={() => removeTripSlot(slot.id)} style={dangerBtn}>
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      ) : null}

      {/* Driver messages */}
      {activeTab === 'messages' ? (
        <section style={section}>
          <h2 style={h2}>Send message to passengers</h2>
          <form onSubmit={sendAdminMessage} style={{ ...cardBox, marginBottom: 20 }}>
            <p style={{ marginTop: 0, color: muted, fontSize: '0.88rem' }}>
              Messages appear on the passenger dashboard. Leave recipient blank to send to everyone.
            </p>
            <label style={{ fontSize: '0.85rem', color: muted }}>Recipient (optional)</label>
            <select
              value={adminMessageTarget}
              onChange={(e) => setAdminMessageTarget(e.target.value)}
              style={{ ...input, width: '100%', marginTop: 6, marginBottom: 12 }}
            >
              <option value="">All passengers</option>
              {passengers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.full_name || 'Passenger'}
                </option>
              ))}
            </select>
            <label style={{ fontSize: '0.85rem', color: muted }}>Message</label>
            <textarea
              required
              value={adminMessage}
              onChange={(e) => setAdminMessage(e.target.value)}
              rows={3}
              style={{ ...input, width: '100%', boxSizing: 'border-box', marginTop: 6, marginBottom: 12, resize: 'vertical' }}
              placeholder="e.g. Buses will leave 15 minutes later today due to weather."
            />
            <button type="submit" disabled={sendingAdminMessage || !adminMessage.trim()} style={primaryBtn}>
              {sendingAdminMessage ? 'Sending…' : 'Send message'}
            </button>
          </form>

          <h2 style={h2}>Driver updates &amp; issues</h2>
          {driverIssues.length === 0 ? (
            <p style={{ color: muted }}>No messages from drivers yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {driverIssues.map((issue) => (
                <div key={issue.id} style={{ ...cardBox, opacity: issue.read_at ? 0.75 : 1 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: '1 1 240px' }}>
                      <strong>{driverNames[issue.driver_id] || 'Driver'}</strong>
                      <div style={{ color: muted, fontSize: '0.82rem', marginTop: 4 }}>
                        {new Date(issue.created_at).toLocaleString()}
                        {issue.read_at ? ' · Read' : ' · Unread'}
                      </div>
                      <p style={{ margin: '10px 0 0', lineHeight: 1.5 }}>{issue.message}</p>
                    </div>
                    {!issue.read_at ? (
                      <button type="button" onClick={() => markIssueRead(issue.id)} style={ghostBtn}>
                        Mark read
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      ) : null}

      {/* Assignments — manual overrides after auto-assign */}
      {activeTab === 'assignments' ? (
        <section style={section}>
          <h2 style={h2}>Today&apos;s assignments</h2>
          <p style={{ color: muted, fontSize: '0.9rem', marginTop: 0 }}>
            The app assigns drivers, areas, and passengers automatically. Use the controls below to rearrange anything
            for today ({localTodayStr()}).
          </p>

          {areasGoingToday.length > 0 ? (
            <div style={{ ...cardBox, marginBottom: 16 }}>
              <div style={{ color: muted, fontSize: '0.88rem', marginBottom: 10 }}>Re-run auto-assign by area</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {areasGoingToday.map((area) => (
                  <button key={area} type="button" onClick={() => handleRebalanceArea(area)} style={ghostBtn}>
                    Auto-assign {area}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {unassignedGoing.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: '1rem', margin: '0 0 10px', color: '#fbbc04' }}>
                Going today — no bus ({unassignedGoing.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {unassignedGoing.map((row) => {
                  const isGuest = !!row.guest_passenger_id;
                  return (
                    <div key={row.id} style={cardBox}>
                      <strong>{goingPersonLabel(row)}</strong>
                      <div style={{ color: muted, fontSize: '0.88rem', marginTop: 4 }}>
                        {goingPersonArea(row)} · {row.seat_count} seat{row.seat_count !== 1 ? 's' : ''}
                      </div>
                      {isGuest ? (
                        <p style={{ color: muted, fontSize: '0.82rem', margin: '10px 0 0' }}>
                          Guest passengers are auto-assigned when you re-run auto-assign for their area.
                        </p>
                      ) : (
                        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                          <label style={{ ...label, marginBottom: 0 }}>Assign to bus</label>
                          <select
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) handleMovePassenger(row.passenger_id, e.target.value);
                            }}
                            style={{ ...input, maxWidth: 220 }}
                          >
                            <option value="">Select bus…</option>
                            {buses.map((b) => (
                              <option key={b.id} value={b.id}>
                                {b.label} ({b.seat_capacity} seats)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {buses.length === 0 ? (
              <p style={{ color: muted }}>Add buses first, then passengers can mark going.</p>
            ) : (
              buses.map((bus) => {
                const usage = busSeatUsage(bus.id);
                const rows = goingByBusId[bus.id] || [];
                return (
                  <div key={bus.id} style={cardBox}>
                    <strong style={{ fontSize: '1.05rem' }}>{bus.label}</strong>
                    <div style={{ color: muted, fontSize: '0.88rem', marginTop: 4 }}>
                      {bus.bus_type === 'auto' ? 'Automatic' : 'Manual'} · {bus.seat_capacity} seats ·{' '}
                      {usage.seats}/{bus.seat_capacity} seats confirmed
                      {usage.waitlisted > 0 ? ` · ${usage.waitlisted} waitlisted` : ''}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 14 }}>
                      <div>
                        <label style={label}>Driver</label>
                        <select
                          value={bus.driver_id || ''}
                          onChange={(e) => handleAssignDriver(bus.id, e.target.value)}
                          style={input}
                        >
                          <option value="">— Unassigned —</option>
                          {driversForBus(bus).map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.full_name || d.id.slice(0, 8)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={label}>Service area</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input
                            value={areaDraftsByBusId[bus.id] ?? ''}
                            onChange={(e) =>
                              setAreaDraftsByBusId((prev) => ({ ...prev, [bus.id]: e.target.value }))
                            }
                            style={input}
                            placeholder="e.g. Gateshead"
                          />
                          <button type="button" onClick={() => handleSaveBusArea(bus.id)} style={primaryBtn}>
                            Save
                          </button>
                        </div>
                      </div>
                    </div>

                    {rows.length > 0 ? (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ color: muted, fontSize: '0.85rem', marginBottom: 8 }}>Passengers on this bus</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {rows.map((row) => {
                            const isGuest = !!row.guest_passenger_id;
                            return (
                              <div
                                key={row.id}
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 10,
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '10px 12px',
                                  borderRadius: 10,
                                  background: 'rgba(0,0,0,0.2)',
                                }}
                              >
                                <div>
                                  <strong style={{ fontSize: '0.95rem' }}>{goingPersonLabel(row)}</strong>
                                  <div style={{ color: muted, fontSize: '0.82rem', marginTop: 2 }}>
                                    {goingPersonArea(row)} · {row.seat_count} seat{row.seat_count !== 1 ? 's' : ''} ·{' '}
                                    <span
                                      style={{
                                        color: row.assignment_status === 'confirmed' ? '#81c995' : '#fbbc04',
                                      }}
                                    >
                                      {ASSIGNMENT_LABELS[row.assignment_status] || row.assignment_status}
                                    </span>
                                  </div>
                                </div>
                                {isGuest ? null : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                                  <select
                                    value={row.bus_id || ''}
                                    onChange={(e) => handleMovePassenger(row.passenger_id, e.target.value)}
                                    style={{ ...input, width: 'auto', minWidth: 140 }}
                                    aria-label="Move to bus"
                                  >
                                    {buses.map((b) => (
                                      <option key={b.id} value={b.id}>
                                        {b.label}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    value={row.assignment_status}
                                    onChange={(e) => handlePassengerStatus(row.passenger_id, e.target.value)}
                                    style={{ ...input, width: 'auto', minWidth: 120 }}
                                    aria-label="Status"
                                  >
                                    <option value="confirmed">Confirmed</option>
                                    <option value="waitlisted">Waitlisted</option>
                                  </select>
                                </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <p style={{ color: muted, fontSize: '0.88rem', marginTop: 12, marginBottom: 0 }}>
                        No passengers assigned to this bus today.
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>
      ) : null}

      {/* Buses */}
      {activeTab === 'buses' ? (
        <section style={section}>
          <h2 style={h2}>Buses</h2>
          <form onSubmit={addBus} style={{ ...cardBox, marginBottom: 16 }}>
            <p style={{ marginTop: 0, color: muted, fontSize: '0.9rem' }}>
              Add buses with seat capacity and type. Routes are assigned automatically from passenger areas — route name is
              optional and for display only.
            </p>
            <div style={formGrid}>
              <div>
                <label style={label}>Label</label>
                <input required value={busLabel} onChange={(e) => setBusLabel(e.target.value)} style={input} placeholder="Bus 1" />
              </div>
              <div>
                <label style={label}>Plate</label>
                <input value={busPlate} onChange={(e) => setBusPlate(e.target.value)} style={input} placeholder="ABC-123" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={label}>Route name (optional)</label>
                <input value={busRoute} onChange={(e) => setBusRoute(e.target.value)} style={input} placeholder="Optional label only" />
              </div>
              <div>
                <label style={label}>Bus type</label>
                <select value={busType} onChange={(e) => setBusType(e.target.value)} style={input}>
                  {BUS_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={label}>Seat capacity</label>
                <input
                  type="number"
                  min={1}
                  required
                  value={busSeats}
                  onChange={(e) => setBusSeats(e.target.value)}
                  style={input}
                />
              </div>
            </div>
            <button type="submit" style={primaryBtn}>
              Add bus
            </button>
          </form>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {buses.length === 0 ? (
              <p style={{ color: muted }}>No buses yet.</p>
            ) : (
              buses.map((bus) => (
                <div key={bus.id} style={cardBox}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <strong style={{ fontSize: '1.05rem' }}>{bus.label}</strong>
                      <div style={{ color: muted, fontSize: '0.88rem', marginTop: 4 }}>
                        {bus.plate_number || 'No plate'} · {bus.route_name || 'No route'}
                      </div>
                      <div style={{ color: muted, fontSize: '0.85rem', marginTop: 6 }}>
                        {bus.bus_type === 'auto' ? 'Automatic' : 'Manual'} · {bus.seat_capacity} seats
                        {bus.service_area ? ` · Assigned area: ${bus.service_area}` : ' · Not yet assigned to an area'}
                      </div>
                      <div style={{ marginTop: 8, fontSize: '0.88rem' }}>
                        Status:{' '}
                        <span style={{ color: bus.is_available ? '#81c995' : '#f28b82' }}>
                          {bus.is_available ? 'Available' : 'Unavailable'}
                        </span>
                        {' · '}
                        Driver:{' '}
                        {bus.driver_id
                          ? drivers.find((d) => d.id === bus.driver_id)?.full_name || bus.driver_id.slice(0, 8)
                          : 'Auto-assign when online'}
                      </div>

                      <div style={{ marginTop: 12 }}>
                        <div style={{ color: muted, fontSize: '0.88rem', marginBottom: 6 }}>Scheduled departure</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                          <input
                            type="time"
                            value={programDraftsByBusId[bus.id] ?? (programsByBusId[bus.id]?.departure_time ? timeToHHMM(programsByBusId[bus.id].departure_time) : '')}
                            onChange={(e) =>
                              setProgramDraftsByBusId((prev) => ({
                                ...prev,
                                [bus.id]: e.target.value,
                              }))
                            }
                            style={{ ...input, maxWidth: 160, padding: '10px 10px' }}
                          />
                          <button type="button" onClick={() => upsertBusDepartureTime(bus.id)} style={primaryBtn}>
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200 }}>
                      <label style={label}>Override driver</label>
                      <select
                        value={bus.driver_id || ''}
                        onChange={(e) => handleAssignDriver(bus.id, e.target.value)}
                        style={input}
                      >
                        <option value="">— Unassigned —</option>
                        {driversForBus(bus).map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.full_name || d.id.slice(0, 8)}
                          </option>
                        ))}
                      </select>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <button type="button" onClick={() => toggleBusAvailability(bus)} style={ghostBtn}>
                          Toggle availability
                        </button>
                        <button type="button" onClick={() => removeBus(bus.id)} style={dangerBtn}>
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      ) : null}

      {/* Drivers */}
      {activeTab === 'drivers' ? (
        <section style={section}>
          <h2 style={h2}>Drivers</h2>
          <div style={cardBox}>
            <p style={{ marginTop: 0, color: muted, fontSize: '0.9rem', lineHeight: 1.5 }}>
              Share the registration link with <code style={{ color: accent }}>?role=driver</code>. New drivers stay pending
              until you approve them below.
            </p>
            {driverInviteUrl ? (
              <div
                style={{
                  padding: 12,
                  borderRadius: 10,
                  background: 'rgba(0,0,0,0.25)',
                  wordBreak: 'break-all',
                  fontSize: '0.85rem',
                  color: text,
                }}
              >
                {driverInviteUrl}
              </div>
            ) : null}
          </div>

          {pendingDrivers.length > 0 ? (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: '1rem', margin: '0 0 10px', color: '#fbbc04' }}>Pending approval ({pendingDrivers.length})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {pendingDrivers.map((dr) => (
                  <div key={dr.id} style={cardBox}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                      <div>
                        <strong>{dr.full_name || 'Driver'}</strong>
                        <div style={{ color: muted, fontSize: '0.88rem', marginTop: 4 }}>
                          Licence: {licenseTypeLabel(dr.driver_license_type)}
                          {dr.driver_license_file_path ? ' · File uploaded' : ' · No file yet'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {dr.driver_license_file_path ? (
                          <button type="button" onClick={() => viewDriverLicense(dr.id, dr.driver_license_file_path)} style={ghostBtn}>
                            View licence
                          </button>
                        ) : null}
                        <button type="button" onClick={() => reviewDriver(dr.id, true)} style={primaryBtn}>
                          Approve
                        </button>
                        <button type="button" onClick={() => reviewDriver(dr.id, false)} style={dangerBtn}>
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
            <h3 style={{ fontSize: '1rem', margin: 0, color: text }}>Approved drivers</h3>
            {approvedDrivers.length === 0 ? (
              <p style={{ color: muted }}>No approved drivers yet.</p>
            ) : (
              approvedDrivers.map((dr) => (
                <div key={dr.id} style={cardBox}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <strong>{dr.full_name || 'Driver'}</strong>
                      <div style={{ color: muted, fontSize: '0.88rem', marginTop: 4 }}>
                        Licence: {licenseTypeLabel(dr.driver_license_type)} · Online: {dr.driver_online ? 'yes' : 'no'} ·{' '}
                        <span style={{ color: dr.active ? '#81c995' : '#f28b82' }}>{dr.active ? 'Active' : 'Deactivated'}</span>
                        {dr.approval_status === 'rejected' ? ' · Rejected' : ''}
                        {dr.driver_license_file_path ? ' · File on file' : ''}
                      </div>
                    </div>
                    {dr.approval_status === 'approved' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 180 }}>
                        {dr.driver_license_file_path ? (
                          <button type="button" onClick={() => viewDriverLicense(dr.id, dr.driver_license_file_path)} style={ghostBtn}>
                            View licence file
                          </button>
                        ) : null}
                        <select
                          value={dr.driver_license_type || ''}
                          onChange={(e) => updateDriverLicense(dr.id, e.target.value)}
                          style={input}
                        >
                          <option value="">Set licence…</option>
                          {DRIVER_LICENSE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <button type="button" onClick={() => deactivateDriver(dr.id, !dr.active)} style={dr.active ? dangerBtn : primaryBtn}>
                          {dr.active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      ) : null}

      {/* Passengers */}
      {activeTab === 'passengers' ? (
        <section style={{ ...section, paddingBottom: 48 }}>
          <h2 style={h2}>Add unregistered passenger</h2>
          <p style={{ color: muted, fontSize: '0.9rem', marginTop: 0 }}>
            Add someone who does not have an account. They appear on driver pickup lists when you mark them going for
            today&apos;s trips.
          </p>
          <form onSubmit={addGuestPassenger} style={{ ...cardBox, marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <div>
                <label style={{ fontSize: '0.85rem', color: muted }}>Full name</label>
                <input required value={guestName} onChange={(e) => setGuestName(e.target.value)} style={{ ...input, width: '100%', marginTop: 6 }} />
              </div>
              <div>
                <label style={{ fontSize: '0.85rem', color: muted }}>Phone</label>
                <input type="tel" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} style={{ ...input, width: '100%', marginTop: 6 }} />
              </div>
              <div>
                <label style={{ fontSize: '0.85rem', color: muted }}>Pickup area</label>
                <input required value={guestArea} onChange={(e) => setGuestArea(e.target.value)} style={{ ...input, width: '100%', marginTop: 6 }} />
              </div>
              <div>
                <label style={{ fontSize: '0.85rem', color: muted }}>Address (optional)</label>
                <input value={guestAddress} onChange={(e) => setGuestAddress(e.target.value)} style={{ ...input, width: '100%', marginTop: 6 }} />
              </div>
              <div>
                <label style={{ fontSize: '0.85rem', color: muted }}>Age range</label>
                <select value={guestAgeRange} onChange={(e) => setGuestAgeRange(e.target.value)} style={{ ...input, width: '100%', marginTop: 6 }}>
                  <option value="adult">Adult (18+)</option>
                  <option value="child_4_12">Child (4–12 years)</option>
                  <option value="child_0_3">Child (3 years and under)</option>
                </select>
              </div>
            </div>
            <button type="submit" disabled={guestSaving} style={{ ...primaryBtn, marginTop: 14 }}>
              {guestSaving ? 'Adding…' : 'Add unregistered passenger'}
            </button>
          </form>

          {guestPassengers.length > 0 ? (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: '1rem', margin: '0 0 10px' }}>Unregistered passengers ({guestPassengers.length})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {guestPassengers.map((g) => (
                  <div key={g.id} style={cardBox}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12 }}>
                      <div>
                        <strong>{g.full_name}</strong>
                        <span style={{ color: muted, fontSize: '0.82rem', marginLeft: 8 }}>(no account)</span>
                        <div style={{ color: muted, fontSize: '0.88rem', marginTop: 4 }}>
                          {g.pickup_area}
                          {g.pickup_address ? ` · ${g.pickup_address}` : ''}
                          {g.phone ? ` · ${g.phone}` : ''}
                        </div>
                      </div>
                      <button type="button" onClick={() => removeGuestPassenger(g.id)} style={dangerBtn}>
                        Remove
                      </button>
                    </div>
                    {todayActiveSlots.length > 0 ? (
                      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {todayActiveSlots.map((slot) => {
                          const goingRow = guestGoingByGuestAndSlot[`${g.id}:${slot.id}`];
                          const isGoing = !!goingRow;
                          return (
                            <div
                              key={slot.id}
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 8,
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <span style={{ color: muted, fontSize: '0.88rem' }}>
                                {slot.label}
                                {isGoing ? (
                                  <span style={{ color: '#81c995', marginLeft: 8 }}>
                                    · {ASSIGNMENT_LABELS[goingRow.assignment_status] || goingRow.assignment_status}
                                  </span>
                                ) : null}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleGuestGoing(g.id, slot.id, !isGoing)}
                                style={isGoing ? dangerBtn : ghostBtn}
                              >
                                {isGoing ? 'Mark not going' : 'Mark going'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ color: muted, fontSize: '0.82rem', margin: '10px 0 0' }}>No trips scheduled today.</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <h2 style={h2}>Registered passengers</h2>
          <p style={{ color: muted, fontSize: '0.9rem', marginTop: 0 }}>
            Passengers register via QR and are grouped by pickup area. Buses are assigned automatically when they mark
            going for the day.
          </p>
          {passengers.length === 0 ? (
            <p style={{ color: muted }}>
              No passengers yet — share <code style={{ color: accent }}>/register/{org?.slug || 'slug'}</code> or the QR on the Access tab.
            </p>
          ) : (
            passengersByArea.map(([area, group]) => (
              <div key={area} style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '1rem', margin: '0 0 10px', color: accent }}>
                  {area} ({group.length})
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {group.map((p) => (
                    <div key={p.id} style={cardBox}>
                      <strong>{p.full_name || 'Passenger'}</strong>
                      <div style={{ color: muted, fontSize: '0.88rem', marginTop: 4 }}>
                        {AGE_RANGE_LABELS[p.age_range] || 'Age not set'}
                        {p.pickup_address ? ` · ${p.pickup_address}` : ''}
                        {p.phone ? ` · ${p.phone}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </section>
      ) : null}

      {/* Access */}
      {activeTab === 'access' ? (
        <section style={section}>
          <h2 style={h2}>Access</h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center', marginBottom: 16 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: muted, fontSize: '0.9rem', marginBottom: 8 }}>Registration link</div>
              {orgRegisterUrl ? (
                <a
                  href={orgRegisterUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: accent, wordBreak: 'break-all', fontWeight: 600 }}
                >
                  {orgRegisterUrl}
                </a>
              ) : (
                <p style={{ color: muted, margin: 0 }}>Organisation slug not found.</p>
              )}

              <div
                style={{
                  marginTop: 16,
                  background: 'rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <div style={{ color: muted, fontSize: '0.85rem', marginBottom: 6 }}>Invite code</div>
                <div style={{ fontSize: '1.7rem', fontWeight: 800, letterSpacing: '0.02em' }}>{inviteCode || '—'}</div>
              </div>
            </div>

            <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 12 }}>
              {orgRegisterUrl ? <QRCodeCanvas value={orgRegisterUrl} size={180} fgColor={accent} bgColor="transparent" /> : null}
            </div>
          </div>

          <form onSubmit={changeInviteCode} style={{ ...cardBox, marginBottom: 16 }}>
            <div style={{ color: muted, fontSize: '0.9rem', marginBottom: 10 }}>Change invite code</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                value={inviteDraft}
                onChange={(e) => setInviteDraft(e.target.value)}
                style={{ ...input, flex: '1 1 240px' }}
                placeholder="New invite code"
                required
                disabled={inviteSaving}
              />
              <button type="submit" style={primaryBtn} disabled={inviteSaving}>
                {inviteSaving ? 'Saving…' : 'Change invite code'}
              </button>
            </div>
          </form>

          <div style={cardBox}>
            <h3 style={{ margin: '0 0 10px', color: text, fontSize: '1rem' }}>How to share</h3>
            <ul style={{ margin: 0, paddingLeft: 18, color: muted, lineHeight: 1.6 }}>
              <li>Print and display at entrance</li>
              <li>Share via WhatsApp</li>
              <li>Add to newsletters</li>
            </ul>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ ...cardBox, textAlign: 'center' }}>
      <div style={{ fontSize: '1.75rem', fontWeight: 700, color: accent }}>{value}</div>
      <div style={{ color: muted, fontSize: '0.85rem', marginTop: 4 }}>{label}</div>
    </div>
  );
}

const shell = {
  minHeight: '100vh',
  background: bg,
  color: text,
  fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  padding: '20px 16px 40px',
  maxWidth: 960,
  margin: '0 auto',
  boxSizing: 'border-box',
};

const headerBar = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 24,
  paddingBottom: 16,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const section = { marginBottom: 28 };
const h2 = { fontSize: '1.15rem', margin: '0 0 12px', color: text };

const cardBox = {
  background: card,
  borderRadius: 12,
  padding: 16,
  border: '1px solid rgba(255,255,255,0.06)',
};

const formGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 12,
  marginBottom: 12,
};

const label = { display: 'block', marginBottom: 6, fontSize: '0.8rem', color: muted };

const input = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.12)',
  background: '#0f1419',
  color: text,
  fontSize: '0.95rem',
};

const primaryBtn = {
  padding: '10px 16px',
  borderRadius: 10,
  border: 'none',
  background: `linear-gradient(180deg, ${accent}, #0a5c5f)`,
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};

const ghostBtn = {
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'transparent',
  color: text,
  cursor: 'pointer',
};

const dangerBtn = {
  ...ghostBtn,
  borderColor: 'rgba(242,139,130,0.5)',
  color: '#f28b82',
};

const tabsBar = {
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
  marginBottom: 18,
};

const tabBtn = {
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'transparent',
  color: text,
  cursor: 'pointer',
  fontWeight: 600,
};

const tabBtnActive = {
  ...tabBtn,
  borderColor: 'rgba(13,115,119,0.9)',
  background: 'rgba(13,115,119,0.14)',
};
